import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, MoreVertical, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const ROLE_STYLES = {
  Admin: "bg-slate-900 text-white",
  Manager: "bg-indigo-600 text-white",
  Employee: "bg-emerald-600 text-white",
};

const EMPTY_FORM = { email: "", name: "", password: "", role: "Employee", manager_id: "none" };

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isAdmin = user.role === "Admin";

  const load = useCallback(async () => {
    try {
      // Admins get the full admin list (with manager_id, is_active); others use public list
      const path = isAdmin ? "/admin/users" : "/users";
      const { data } = await api.get(path);
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const managers = useMemo(() => users.filter((u) => u.role === "Manager" && u.is_active !== false), [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users;
    if (user.role === "Manager") {
      // Managers see only own team + self
      list = list.filter((u) => u.id === user.id || u.manager_id === user.id || u.role !== "Employee");
    }
    return list
      .filter((u) => (roleFilter === "all" ? true : u.role === roleFilter))
      .filter((u) => (q ? u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) : true))
      .sort((a, b) => (a.role || "").localeCompare(b.role || "") || a.name.localeCompare(b.name));
  }, [users, roleFilter, search, user]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name,
      password: "",
      role: u.role,
      manager_id: u.manager_id || "none",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const patch = {
          name: form.name,
          role: form.role,
          manager_id: form.role === "Employee" ? (form.manager_id === "none" ? "" : form.manager_id) : null,
        };
        if (form.password) patch.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, patch);
        toast.success("Member updated");
      } else {
        const payload = {
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          password: form.password,
          role: form.role,
          manager_id: form.role === "Employee" && form.manager_id !== "none" ? form.manager_id : null,
        };
        await api.post("/admin/users", payload);
        toast.success("Team member added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Member deactivated" : "Member reactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/users/${confirmDelete.id}`);
      toast.success("Member deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="label-eyebrow">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Team</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
            {isAdmin ? "Team management" : "My team"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {isAdmin
              ? "Add, edit, deactivate or delete team members. Assign managers to employees."
              : "The employees under your management."}
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={openCreate}
            className="bg-slate-900 hover:bg-slate-800 text-white transition-transform hover:-translate-y-[1px]"
            data-testid="create-user-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add member
          </Button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-3 bg-white border border-slate-200 rounded-md p-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="team-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]" data-testid="team-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="Admin">Admin</SelectItem>
            <SelectItem value="Manager">Manager</SelectItem>
            <SelectItem value="Employee">Employee</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Name</th>
                <th className="px-4 py-3 label-eyebrow hidden md:table-cell">Email</th>
                <th className="px-4 py-3 label-eyebrow">Role</th>
                <th className="px-4 py-3 label-eyebrow hidden lg:table-cell">Manager</th>
                {isAdmin && <th className="px-4 py-3 label-eyebrow">Status</th>}
                {isAdmin && <th className="px-4 py-3 label-eyebrow text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 4} className="px-4 py-10 text-center text-slate-500">
                    No team members found.
                  </td>
                </tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid={`team-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-slate-100 border border-slate-200 rounded-full flex items-center justify-center text-xs font-bold text-slate-700">
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{u.name}</div>
                        <div className="text-xs text-slate-500 md:hidden">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-bold ${ROLE_STYLES[u.role] || "bg-slate-500 text-white"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                    {u.role === "Employee" ? (u.manager_name || <span className="text-slate-400">—</span>) : "—"}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      {u.is_active !== false ? (
                        <span className="text-xs text-emerald-700 font-semibold">Active</span>
                      ) : (
                        <span className="text-xs text-slate-500 font-semibold">Inactive</span>
                      )}
                    </td>
                  )}
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`team-actions-${u.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`team-edit-${u.id}`}>
                            Edit
                          </DropdownMenuItem>
                          {u.id !== user.id && (
                            <>
                              <DropdownMenuItem onClick={() => toggleActive(u)} data-testid={`team-toggle-active-${u.id}`}>
                                {u.is_active !== false ? "Deactivate" : "Reactivate"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                                onClick={() => setConfirmDelete(u)}
                                data-testid={`team-delete-${u.id}`}
                              >
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="team-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit member" : "Add team member"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {editing
                ? "Update details, role, or reset password."
                : "Provide a temporary password. The member can change it after first login."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-eyebrow" htmlFor="tf-name">Name</Label>
                <Input
                  id="tf-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="team-form-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow" htmlFor="tf-email">Email</Label>
                <Input
                  id="tf-email"
                  type="email"
                  required
                  disabled={!!editing}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="team-form-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-eyebrow">Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm({ ...form, role: v, manager_id: v === "Employee" ? form.manager_id : "none" })}
                >
                  <SelectTrigger data-testid="team-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Employee">Employee</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-eyebrow" htmlFor="tf-password">
                  {editing ? "New password (optional)" : "Temporary password"}
                </Label>
                <Input
                  id="tf-password"
                  type="password"
                  required={!editing}
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="team-form-password"
                />
              </div>
            </div>
            {form.role === "Employee" && (
              <div className="space-y-2">
                <Label className="label-eyebrow">Reporting manager</Label>
                <Select
                  value={form.manager_id}
                  onValueChange={(v) => setForm({ ...form, manager_id: v })}
                >
                  <SelectTrigger data-testid="team-form-manager"><SelectValue placeholder="Select a manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No manager</SelectItem>
                    {managers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="team-form-submit"
              >
                {busy ? "Saving…" : editing ? "Save changes" : "Add member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="team-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the member. Any tasks assigned to them will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="team-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
